/**
 * Distribution API DataSource
 *
 * This class handles all API communication for the distribution feature.
 * It provides a clean interface for making HTTP requests to the backend.
 *
 * @module DistributionAPIDataSource
 */

import {Http} from '../../../Data/config/Http';
import {API_ROUTES} from '../../../constants/apiRoutes';
import type {BaseResponse} from '../../../Data/types/BaseResponse';
import {PAGINATION_CONFIG} from '../../../constants/appConfig';
import {unwrapResponse, handleDataSourceError} from '../../../utils/errorHelpers';

import {
  DistributionRecordDTO,
  DistributionMarketerDTO,
  DistributionProductDTO,
  AvailableSerialDTO,
  CreateDeliveryRequestDTO,
  UpdateDeliveryRequestDTO,
  DistributionFiltersDTO
} from '../models/distribution.dto';

export class DistributionAPIDataSource {

  // Distribution Records
  static async getDistributionHistory (filters?: DistributionFiltersDTO, page = 1, limit: number = PAGINATION_CONFIG.DEFAULT_PAGE_SIZE): Promise<{ items: DistributionRecordDTO[]; pagination?: any }> {
    try {
      const allParams = {
        ...filters,
        page,
        limit
      };

      const params = new URLSearchParams(
        Object.entries(allParams).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString();

      const url = `${API_ROUTES.DISTRIBUTION.HISTORY}?${params}`;

      const response = await Http.get<BaseResponse<DistributionRecordDTO[]>>(url);
      const raw = response.data;
      const items = unwrapResponse(raw, 'فشل في جلب تاريخ التوزيع');
      return {
        items: Array.isArray(items) ? items : [],
        pagination: raw.pagination
      };
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Marketers for Distribution (Updated to use new API with search support)
  static async getMarketersForDistribution (search?: string): Promise<DistributionMarketerDTO[]> {
    try {
      const params = search ? {search} : {};
      const response = await Http.get<BaseResponse<DistributionMarketerDTO[]>>(API_ROUTES.DISTRIBUTION.MARKETERS_ALL, {params});
      return unwrapResponse(response.data, 'فشل في جلب المسوقين للتوزيع');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Products for Distribution (Updated to use new API with search support)
  static async getProductsForDistribution (search?: string): Promise<DistributionProductDTO[]> {
    try {
      const params = search ? {search} : {};
      const response = await Http.get<BaseResponse<DistributionProductDTO[]>>(API_ROUTES.DISTRIBUTION.PRODUCTS_ALL, {params});
      return unwrapResponse(response.data, 'فشل في جلب المنتجات للتوزيع');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Available Serials for Product
  static async getAvailableSerialsForProduct (productId: string): Promise<AvailableSerialDTO[]> {
    try {
      const response = await Http.get<BaseResponse<AvailableSerialDTO[]>>(API_ROUTES.DISTRIBUTION.AVAILABLE_SERIALS_BY_PRODUCT(productId));
      return unwrapResponse(response.data, 'فشل في جلب الأرقام التسلسلية المتاحة للمنتج');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Create Delivery
  static async createDelivery (deliveryData: CreateDeliveryRequestDTO): Promise<DistributionRecordDTO> {
    try {
      const response = await Http.post<BaseResponse<DistributionRecordDTO>>(API_ROUTES.DISTRIBUTION.DELIVERY, deliveryData);
      return unwrapResponse(response.data, 'فشل في إنشاء التسليم', [200, 201]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Update Delivery
  static async updateDelivery (id: string, deliveryData: UpdateDeliveryRequestDTO): Promise<DistributionRecordDTO> {
    try {
      const response = await Http.put<BaseResponse<DistributionRecordDTO>>(API_ROUTES.DISTRIBUTION.DELIVERY_BY_ID(id), deliveryData);
      return unwrapResponse(response.data, 'فشل في تحديث التسليم');
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Delete Transaction
  static async deleteTransaction (transactionId: string): Promise<void> {
    try {
      const response = await Http.delete<BaseResponse<void>>(API_ROUTES.DISTRIBUTION.DELETE_TRANSACTION(transactionId));
      unwrapResponse(response.data, 'فشل في حذف المعاملة', [200, 204]);
    } catch (error) {
      handleDataSourceError(error);
    }
  }

  // Get Tracked Serials
  static async getTrackedSerials (filters?: { product_id?: string; location_id?: string; status?: string }): Promise<AvailableSerialDTO[]> {
    try {
      const params = filters ? new URLSearchParams(
        Object.entries(filters).reduce((acc, [key, value]) => {
          if (value !== undefined && value !== null) {
            acc[key] = value.toString();
          }
          return acc;
        }, {} as Record<string, string>)
      ).toString() : '';

      const url = params ? `${API_ROUTES.DISTRIBUTION.ALL_SERIALS}?${params}` : API_ROUTES.DISTRIBUTION.ALL_SERIALS;

      const response = await Http.get<BaseResponse<AvailableSerialDTO[]>>(url);
      return unwrapResponse(response.data, 'فشل في جلب الأرقام التسلسلية المتتبعة');
    } catch (error) {
      handleDataSourceError(error);
    }
  }
}
